import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import { Loader, ErrorBanner } from '../../components/common/Feedback';
import * as reportsApi from '../../api/reports';
import { formatCurrency } from '../../utils/format';

const TABS = ['Financial', 'Projects', 'Employees'];

export default function Reports() {
  const [tab, setTab] = useState('Financial');
  const [financial, setFinancial] = useState(null);
  const [projectReport, setProjectReport] = useState(null);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    reportsApi.fetchFinancialReport({}).then((res) => setFinancial(res.data)).catch((err) => setError(err.response?.data?.message || 'Failed to load report.'));
    reportsApi.fetchProjectReport().then((res) => setProjectReport(res.data)).catch(() => {});
    reportsApi.fetchEmployeeReport().then((res) => setEmployeeReport(res.data)).catch(() => {});
  }, []);

  return (
    <DashboardLayout title="Reports">
      <div className="page-header">
        <div>
          <h1 className="heading">Reports & Analytics</h1>
          <p className="page-subtext">Financial, project and employee performance at a glance.</p>
        </div>
      </div>

      <ErrorBanner message={error} />

      <div className="tab-bar">
        {TABS.map((t) => (
          <button key={t} type="button" className={`tab-item${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Financial' && (
        !financial ? <Loader text="Loading financial report..." /> : (
          <>
            <div className="card-row">
              <div className="stat-card"><div className="stat-label">Total Invoiced</div><div className="stat-value">{formatCurrency(financial.totalInvoiced)}</div></div>
              <div className="stat-card"><div className="stat-label">Total Collected</div><div className="stat-value">{formatCurrency(financial.totalCollected)}</div></div>
              <div className="stat-card"><div className="stat-label">Outstanding</div><div className="stat-value">{formatCurrency(financial.outstanding)}</div></div>
              <div className="stat-card"><div className="stat-label">Overdue Invoices</div><div className="stat-value">{financial.overdueCount}</div></div>
            </div>
            <div className="card">
              <h3 className="subheading">Invoices by Status</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={financial.byStatus.map((s) => ({ status: s.status, amount: Number(s.amount || 0) }))}>
                  <CartesianGrid stroke="#e5e6e9" />
                  <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="amount" fill="#011a40" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <a className="btn-link" href={reportsApi.financialReportExportUrl()} target="_blank" rel="noreferrer">Export Financial Report (CSV)</a>
          </>
        )
      )}

      {tab === 'Projects' && (
        !projectReport ? <Loader text="Loading project report..." /> : (
          <>
            <div className="card-row">
              <div className="stat-card"><div className="stat-label">Delayed Projects</div><div className="stat-value">{projectReport.delayedProjects}</div></div>
            </div>
            <div className="card">
              <h3 className="subheading">Projects by Status</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={projectReport.byStatus}>
                  <CartesianGrid stroke="#e5e6e9" />
                  <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#011a40" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )
      )}

      {tab === 'Employees' && (
        !employeeReport ? <Loader text="Loading employee report..." /> : (
          <>
            <DataTable
              columns={[
                { key: 'name', label: 'Employee' },
                { key: 'department', label: 'Department', render: (r) => r.department || '-' },
                { key: 'totalTasks', label: 'Total Tasks' },
                { key: 'completedTasks', label: 'Completed' },
                { key: 'completionRate', label: 'Completion %', render: (r) => `${r.completionRate}%` },
                { key: 'hoursLogged', label: 'Hours Logged' },
              ]}
              rows={employeeReport}
              getRowKey={(r) => r.id}
            />
            <a className="btn-link" href={reportsApi.employeeReportExportUrl()} target="_blank" rel="noreferrer">Export Employee Report (CSV)</a>
          </>
        )
      )}
    </DashboardLayout>
  );
}
